import React, {useState, useRef, useEffect} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import {pxToRem} from 'utils/ui/units';
import queryparse from 'tricks/string/queryparse';
import createLocation from 'utils/createLocation';
import {addHeapTrackingIfRequired} from 'utils/heapTracking';
import {addMindsetAIIfRequired} from 'utils/mindset';
import {useAuthContext} from '../../contexts/auth';
import {useServerEvents} from '../../contexts/serverEvents';

import changeLanguage from 'utils/i18n/changeLanguage';
import LoginActions from '../../../actions/loginActions';
import config from 'utils/config';
import track, {getSessionToken} from 'utils/track';
import {userStore, LAST_SIGN_IN_METHOD} from 'utils/store';

import Alert from 'components/Alert';
import Box from 'components/Box';
import Button from 'components/Button';
import Logo from 'components/Logo';
import Panel from 'components/Panel';
import Text from 'components/Text';
import Stack from 'components/Stack';
import PageTitle from 'components/PageTitle';
import Status from 'components/Status';

import FormField from 'components/FormField';
import FormStack from 'components/FormStack';
import TextInput from 'components/TextInput';
import PasswordInput from 'components/PasswordInput';

import {useUpdateUserLanguage} from 'actions/userProfile';

import AuthPageWrapper from './shared/AuthPageWrapper';
import {shouldPersistUserLang} from './shared/LanguageSwitcher';
import {formatLanguageCode} from 'i18n/options';
import {hasBadContrast} from 'color2k';

const LOGIN_METHODS = {
	EMAIL: 'email',
	LINK: 'link',
	BUTTON: 'button',
	DOMAIN_BASED: 'domainBased',
};

function storeLastSigninMethod(lastSigninMethod) {
	userStore({
		[LAST_SIGN_IN_METHOD]: lastSigninMethod,
	});
}
function getLastSigninMethod() {
	return userStore(LAST_SIGN_IN_METHOD);
}

export function LogoPanel({subtitle}) {
	const [t] = useTranslation();
	return (
		<Panel align="center">
			<Box mb="xs" style={{height: pxToRem(50)}}>
				<Logo alt={config.orgTitle || t('hub_title')} />
			</Box>
			{subtitle && (
				<Text as="p" size="s" overflow="wrap">
					{subtitle}
				</Text>
			)}
		</Panel>
	);
}

const TextPreviously = styled.span(p => {
	const {
		background,
		globals: {
			buttons: {primary},
		},
	} = p.theme;
	const color = hasBadContrast(primary.text, 'readable', background)
		? primary.background
		: primary.text;
	return {
		color,
		fontWeight: 'bold',
	};
});

function Login() {
	const {t, i18n} = useTranslation();
	const location = useLocation();
	const queryLoginMethod = queryparse(location.search)?.login;
	const lastSigninMethod = getLastSigninMethod();

	const {isAuthenticating, setAuthUser} = useAuthContext();

	const [showMaintenanceNote, setShowMaintenanceNote] = useState(
		config.status && config.status !== 'active'
	);

	const [email, setEmail] = useState(
		() => queryparse(location.search)?.email || ''
	);

	const [errorMessage, setErrorMessage] = useState('');

	// Check if there's a custom name for SSO login. This is used to
	// decide if federated login can be displayed
	// otherwise was it's last identity used?
	const federatedLogins =
		config.federatedLogins?.filter(
			({buttonSubtitle, identity}) =>
				!!buttonSubtitle || lastSigninMethod === identity
		) || [];

	// use the domain based login form based on the config or query
	const {loginPassword = LOGIN_METHODS.LINK} = config;
	const emailDomainBasedLogin =
		loginPassword === LOGIN_METHODS.DOMAIN_BASED ||
		queryLoginMethod === LOGIN_METHODS.DOMAIN_BASED;

	const [
		emailDomainBasedLoginUsePassword,
		setEmailDomainBasedLoginUsePassword,
	] = useState(false);

	// Toggles the view from the regular email/password form to a screen
	// that gives a choice between SSO login and email/pw login.
	const [showFederatedLogin, setShowFederatedLogin] = useState(
		!emailDomainBasedLogin &&
			Boolean(federatedLogins.length) &&
			queryLoginMethod !== LOGIN_METHODS.EMAIL &&
			lastSigninMethod !== LOGIN_METHODS.EMAIL
	);

	const emailInputRef = useRef();
	const passwordInputRef = useRef();
	const pageTitleRef = useRef();

	// Set user focus on page load and after switching federation methods
	useEffect(() => {
		const elementToFocus = showFederatedLogin
			? pageTitleRef
			: emailInputRef;
		elementToFocus.current?.focus();
	}, [showFederatedLogin]);

	// Set user focus to password field when using email-domain-based login
	useEffect(() => {
		if (emailDomainBasedLoginUsePassword) {
			passwordInputRef.current.focus();
		}
	}, [emailDomainBasedLoginUsePassword]);

	const {removeAllServerEventListeners} = useServerEvents();

	useEffect(() => {
		removeAllServerEventListeners();
	}, [removeAllServerEventListeners]);

	const updateUserLanguage = useUpdateUserLanguage();

	function handleLogin(event) {
		event.preventDefault();

		setErrorMessage('');

		if (!email && !passwordInputRef.current.value) {
			setErrorMessage(t('login_creds_missing'));
			emailInputRef.current?.focus();
			return;
		}

		if (!email) {
			setErrorMessage(t('login_email_missing'));
			emailInputRef.current?.focus();
			return;
		}

		if (!passwordInputRef.current.value) {
			setErrorMessage(t('login_password_missing'));
			passwordInputRef.current.focus();
			return;
		}

		// Store as the last signin method
		storeLastSigninMethod(LOGIN_METHODS.EMAIL);

		// Trigger login
		const postLogin = LoginActions.postLogin({
			username: email.trim(),
			password: passwordInputRef.current.value,
		});

		postLogin
			.then(data => {
				setAuthUser(data);

				// add Heap Analytics and Mindset AI
				const {id: userId, domain, domainId, isAdmin, role} = data;
				addHeapTrackingIfRequired({
					userId,
					domain,
					domainId,
					isAdmin,
					role,
				});
				addMindsetAIIfRequired();

				const {utm_source, utm_medium} = queryparse(
					document.location.search
				);

				track('user', 'logged-in', utm_medium || '', utm_source);

				// Store the user's language preference if needed
				if (config.userLanguageEnabled && shouldPersistUserLang()) {
					updateUserLanguage(i18n.language);
				} else if (data.language) {
					changeLanguage(formatLanguageCode(data.language));
				}
			})
			.catch(err => {
				setErrorMessage(() => {
					if (err?.message === 'Missing credentials') {
						return t('login_creds_missing');
					} else if (err?.code === 'login_limit_exceeded') {
						return t('login_limit_exceeded', {
							minutes: Math.ceil(err.retry_after / 60),
						});
					} else if (err?.message === 'forbidden') {
						return t('login_forbidden');
					} else if (err?.status >= 500) {
						return t('error_internal');
					} else {
						return t('login_creds_incorrect');
					}
				});
				emailInputRef.current?.focus();
			});
	}

	function handleDomainBasedLogin(event) {
		event.preventDefault();

		if (!email) {
			setErrorMessage(t('login_email_missing'));
			emailInputRef.current?.focus();
			return;
		}
		setErrorMessage('');

		// get all sso domains which have a domain authority and create a lookup
		// to match email domains to their respective federated login
		const ssoDomains =
			config.federatedLogins?.filter(
				({domainAuthority}) => domainAuthority
			) || [];

		const relayState = {
			sessionToken: getSessionToken(),
			...(redirectAfterLogin && {
				redirectAfterLogin: redirectAfterLogin + location.hash,
			}),
		};

		const ssoDomainsLookup = ssoDomains.reduce(
			(acc, {id, domainAuthority, identity}) => {
				const federatedLogin = `/auth/federation/${id}/login?RelayState=${btoa(
					JSON.stringify(relayState)
				)}`;
				const emailDomains = domainAuthority
					? domainAuthority.split(',')
					: [];

				emailDomains.forEach(domain => {
					acc[domain.trim().toLowerCase()] = {
						federatedLogin,
						identity,
					};
				});
				return acc;
			},
			{}
		);

		// get the domain from the email and match it to an idp
		const domainAuthority = email.trim().split('@').pop().toLowerCase();

		const idp = ssoDomainsLookup[domainAuthority];

		if (idp) {
			const {federatedLogin, identity} = idp;
			storeLastSigninMethod(identity);
			window.location.href = federatedLogin;
		} else {
			setEmailDomainBasedLoginUsePassword(true);
		}
	}

	function toggleFederatedLogin() {
		setShowFederatedLogin(previousState => !previousState);
	}

	function toggleEmailDomainBasedLogin() {
		setErrorMessage('');
		setEmailDomainBasedLoginUsePassword(previousState => !previousState);
	}

	const {redirectAfterLogin} = queryparse(location.search);

	const helpButtons = [];
	const buttons = [];

	// Add SSO login to the button menu
	if (!emailDomainBasedLogin && federatedLogins.length) {
		if (showFederatedLogin) {
			const relayState = {
				sessionToken: getSessionToken(),
				...(redirectAfterLogin && {
					redirectAfterLogin: redirectAfterLogin + location.hash,
				}),
			};

			// Sort the federatedLogins alphabetically
			federatedLogins.sort(({buttonSubtitle: a}, {buttonSubtitle: b}) => {
				// -1, true(1) || false (0)
				return a < b ? -1 : a > b;
			});

			const btns = federatedLogins.map(
				({id, buttonSubtitle, identity}) => {
					const ssoButtonTitle = t('login_with_sso', {
						name: buttonSubtitle || identity,
					});

					const federatedLogin = `/auth/federation/${id}/login?RelayState=${btoa(
						JSON.stringify(relayState)
					)}`;

					return {
						id: identity,
						as: 'a',
						href: federatedLogin,
						title: ssoButtonTitle,
						onClick() {
							// Save the state in local storage
							storeLastSigninMethod(identity);
						},
					};
				}
			);

			buttons.push(...btns);
		} else {
			// Get a filtered list of SSO names
			const names = federatedLogins
				?.map(({buttonSubtitle}) => buttonSubtitle)
				.filter(Boolean);

			let title;
			// If only one name exists use that...
			if (names.length === 1) {
				title = t('login_with_sso', {name: names.pop()});
			} else {
				title = t('login_with_different_method');
			}

			helpButtons.push({
				id: 'federated',
				onClick: toggleFederatedLogin,
				title,
			});
		}
	}

	if (
		!emailDomainBasedLogin &&
		[LOGIN_METHODS.BUTTON, LOGIN_METHODS.LINK].includes(loginPassword)
	) {
		// Add an extra btn for signing in with Username and Password
		const defaultLoginButton = {
			id: 'email',
			onClick: toggleFederatedLogin,
			title: t('login_use_5app'),
		};
		if (loginPassword === LOGIN_METHODS.BUTTON) {
			buttons.push(defaultLoginButton);
		} else if (showFederatedLogin && loginPassword === LOGIN_METHODS.LINK) {
			helpButtons.push(defaultLoginButton);
		}
	}

	if (i18n.exists('login_help_message')) {
		// And the Need Help button to show the Help Message
		helpButtons.push({
			id: 'help_button',
			as: Link,
			to: '/login-help',
			title: t('login_help'),
		});
	}

	// What was the last signin method?
	let highlightedButton;

	if (lastSigninMethod && buttons.length > 0) {
		// Splice out from the list of buttons
		const index = buttons.findIndex(({id}) => id === lastSigninMethod);

		if (index >= 0) {
			// Capture that entry
			highlightedButton = buttons.at(index);

			// Remove from list of buttons
			buttons.splice(index, 1);
		}
	}

	if (isAuthenticating) {
		return null;
	}

	if (showMaintenanceNote) {
		const message =
			(config.status === 'maintenance' &&
				config.customMaintenanceModeMessage) ||
			t(`hub_status_${config.status}`);

		return (
			<AuthPageWrapper>
				<LogoPanel />
				<Panel>
					<p>{message}</p>
				</Panel>
				{config.status !== 'deleted' && (
					<Panel align="right">
						<Button
							variant="transparent"
							size="small"
							onClick={() => setShowMaintenanceNote(false)}
							mt="xs"
							mr="-xs"
						>
							{t('login_admin_access')}
						</Button>
					</Panel>
				)}
			</AuthPageWrapper>
		);
	}

	// Making sure the page title is a single string without line breaks
	// to optimise screen reader output.
	const pageTitle = `${t('login_submit')} - ${t('hub_title')}`;

	if (emailDomainBasedLogin && !emailDomainBasedLoginUsePassword) {
		return (
			<AuthPageWrapper>
				<PageTitle visuallyHidden ref={pageTitleRef}>
					{pageTitle}
				</PageTitle>
				<LogoPanel subtitle={t('login_welcome_message')} />
				<form onSubmit={handleDomainBasedLogin}>
					<Panel flush="top">
						<Status role="alert" mb={errorMessage && 'xs'}>
							{errorMessage && (
								<Alert id="invalid_email_error">
									{errorMessage}
								</Alert>
							)}
						</Status>
						<FormStack>
							<FormField label={t('email')}>
								<TextInput
									type="email"
									autoComplete="username"
									spellCheck="false"
									autoCorrect="off"
									placeholder={t('email_placeholder')}
									ref={emailInputRef}
									value={email}
									onChange={e =>
										setEmail(e.target.value.trim())
									}
								/>
							</FormField>
						</FormStack>
					</Panel>

					<Panel align="right">
						<Button
							id="login_to_5app_button"
							type="submit"
							fullWidth
							variant="primary"
							iconRight="arrow-right"
						>
							{t('actions_next')}
						</Button>
						<Box mt="xs" mr="-xs">
							{helpButtons.map(button => (
								<Button
									variant="transparent"
									size="small"
									key={button.id}
									id={button.id}
									as={button.as}
									to={button.to}
									href={button.href}
									onClick={button.onClick}
								>
									{button.title}
								</Button>
							))}
						</Box>
					</Panel>
				</form>
			</AuthPageWrapper>
		);
	}

	if (showFederatedLogin) {
		return (
			<AuthPageWrapper>
				<PageTitle visuallyHidden ref={pageTitleRef}>
					{pageTitle}
				</PageTitle>
				<LogoPanel subtitle={t('login_welcome_message')} />
				<Panel align="right">
					<Stack spacing="s" align="left" aria-label="login-options">
						{highlightedButton && (
							<>
								<TextPreviously id={'login_previously_used'}>
									{t('login_previously_used')}:
								</TextPreviously>
								<Button
									fullWidth
									variant="primary"
									key={highlightedButton.id}
									as={highlightedButton.as}
									href={highlightedButton.href}
									onClick={highlightedButton.onClick}
								>
									{highlightedButton.title}
								</Button>
							</>
						)}
						{buttons.map(button => (
							<Button
								fullWidth
								variant={
									highlightedButton
										? 'default-negative'
										: 'primary'
								}
								key={button.id}
								as={button.as}
								href={button.href}
								onClick={button.onClick}
							>
								{button.title}
							</Button>
						))}
					</Stack>
					{helpButtons.map(button => (
						<Button
							variant="transparent"
							size="small"
							key={button.id}
							id={button.id}
							as={button.as}
							to={button.to}
							href={button.href}
							onClick={button.onClick}
							mt="xs"
							mr="-xs"
						>
							{button.title}
						</Button>
					))}
				</Panel>
			</AuthPageWrapper>
		);
	}

	return (
		<AuthPageWrapper>
			<PageTitle visuallyHidden ref={pageTitleRef}>
				{pageTitle}
			</PageTitle>
			<LogoPanel subtitle={t('login_welcome_message')} />
			<form onSubmit={handleLogin}>
				<Panel flush="top">
					<Status role="alert" mb={errorMessage && 'xs'}>
						{errorMessage && (
							<Alert id="invalid_email_password_error">
								{errorMessage}
							</Alert>
						)}
					</Status>
					<FormStack>
						{!emailDomainBasedLoginUsePassword ? (
							<FormField label={t('email')}>
								<TextInput
									type="email"
									autoComplete="username"
									spellCheck="false"
									autoCorrect="off"
									placeholder={t('email_placeholder')}
									ref={emailInputRef}
									disabled={emailDomainBasedLoginUsePassword}
									value={email}
									onChange={e =>
										setEmail(e.target.value.trim())
									}
								/>
							</FormField>
						) : (
							<p>
								<strong>{t('email_colon')}</strong>
								<br />
								<Text mr="xs">{email} </Text>
								<Button
									variant="transparent"
									size="small"
									mt="-xxxs"
									// Negative margin to compensate for button padding
									// when button wraps to new line, helping visual alignment
									ml="-xs"
									onClick={toggleEmailDomainBasedLogin}
								>
									{t('login_change_email')}
								</Button>
							</p>
						)}
						<FormField label={t('password')}>
							<PasswordInput ref={passwordInputRef} />
						</FormField>
					</FormStack>
				</Panel>

				<Panel align="right">
					<Button
						id="login_to_5app_button"
						type="submit"
						fullWidth
						variant="primary"
					>
						{t('login_submit')}
					</Button>
					<Box mt="xs" mr="-xs">
						{helpButtons.map(button => (
							<Button
								variant="transparent"
								size="small"
								key={button.id}
								id={button.id}
								as={button.as}
								to={button.to}
								href={button.href}
								onClick={button.onClick}
							>
								{button.title}
							</Button>
						))}
						<Button
							as={Link}
							to={createLocation('/forgot', email ? {email} : {})}
							id="forgot_password_button"
							variant="transparent"
							size="small"
						>
							{t('login_forgot_password')}
						</Button>
					</Box>
				</Panel>
			</form>
		</AuthPageWrapper>
	);
}

export default Login;
